'use client';
import React, { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  avatar: string;
  quote: string;
  earned: string;
  reviews: number;
}

interface SideReview {
  name: string;
  city: string;
  text: string;
}

const DEFAULT_TESTIMONIALS: Testimonial[] = [
{ id: 1, name: 'Priya Sharma', role: 'Homemaker, Mumbai', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d60eec99-1782128994962.png", quote: "I've been reviewing products for 8 months. Last month I earned ₹7,200 — enough to cover my grocery bill! The UPI payments arrive within 3 days, no delays.", earned: '₹7,200', reviews: 18 },
{ id: 2, name: 'Rahul Mehta', role: 'Software Engineer, Bangalore', avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c79c0037-1768451674853.png', quote: 'As someone who buys a lot of tech products anyway, getting paid to review them is a no-brainer. The AI quality check keeps the platform honest — no fake reviews allowed.', earned: '₹4,800', reviews: 11 },
{ id: 3, name: 'Anjali Nair', role: 'College Student, Hyderabad', avatar: 'https://images.unsplash.com/photo-1528750178836-ef5c14d9951e', quote: 'Perfect side income for students. I review beauty and skincare products which I would buy anyway. The review guidelines are clear and the selection process is fair.', earned: '₹3,100', reviews: 8 }];


const DEFAULT_SIDE_REVIEWS: SideReview[] = [
{ name: 'Vikram D.', city: 'Delhi', text: '"Finally a platform that pays on time. No excuses, just UPI transfers."' },
{ name: 'Sunita R.', city: 'Pune', text: '"The AI check is genius — it forces you to write honest, detailed reviews."' },
{ name: 'Arjun K.', city: 'Chennai', text: '"Got selected for a smartwatch campaign. Reviewed it honestly, got paid ₹1,500."' }];


export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>(DEFAULT_TESTIMONIALS);
  const [sideReviews, setSideReviews] = useState<SideReview[]>(DEFAULT_SIDE_REVIEWS);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const supabase = createClient();
    Promise.all([
    supabase.from('platform_settings').select('value').eq('key', 'testimonials').maybeSingle(),
    supabase.from('platform_settings').select('value').eq('key', 'side_reviews').maybeSingle()]
    ).then(([tRes, srRes]) => {
      if (tRes.data?.value) {
        try {setTestimonials(JSON.parse(tRes.data.value));} catch {}
      }
      if (srRes.data?.value) {
        try {setSideReviews(JSON.parse(srRes.data.value));} catch {}
      }
    });
  }, []);

  const active = testimonials[Math.min(activeIdx, testimonials.length - 1)];

  return (
    <section className="section-py overflow-hidden bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="mb-10 sm:mb-14">
          <span className="pill-badge mb-4">Community</span>
          <h2 className="font-heading font-bold text-foreground mt-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)' }}>
            Real Reviewers,{' '}
            <span className="gradient-text">Real Earnings.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7 space-y-6 sm:space-y-8">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-border shadow-sm relative overflow-hidden">
              <div className="absolute top-4 right-6 text-primary/8 pointer-events-none select-none font-heading" style={{ fontSize: '8rem', lineHeight: 1, fontWeight: 800 }}>"</div>
              <div className="flex gap-1 mb-5">
                {[1, 2, 3, 4, 5].map((s) => <Icon key={s} name="StarIcon" size={16} variant="solid" className="star-filled" />)}
              </div>
              <p className="font-heading text-lg sm:text-xl md:text-2xl text-foreground leading-snug relative z-10">
                {active?.quote}
              </p>
              <div className="flex items-center gap-4 pt-6 mt-6 border-t border-border">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-border shrink-0">
                  <AppImage src={active?.avatar} alt={`${active?.name} profile photo`} width={48} height={48} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-foreground text-sm">{active?.name}</div>
                  <div className="text-xs text-muted-foreground">{active?.role}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-bold text-xl text-primary">{active?.earned}</div>
                  <div className="text-xs text-muted-foreground">{active?.reviews} reviews</div>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {testimonials.map((_, i) =>
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? 'w-8 bg-primary' : 'w-1.5 bg-border'}`}
                aria-label={`View testimonial ${i + 1}`} />

              )}
            </div>
          </div>

          <div className="lg:col-span-4 lg:col-start-9 space-y-4 sm:space-y-6">
            {sideReviews.map((r, i) =>
            <div key={i} className="bg-white rounded-2xl p-5 border border-border shadow-sm">
                <p className="text-sm text-foreground leading-relaxed mb-3">{r.text}</p>
                <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  — {r.name}, {r.city}
                </span>
              </div>
            )}

            <div className="bg-white rounded-2xl p-5 border border-border shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="TrophyIcon" size={18} className="text-primary" />
                <span className="text-xs font-bold text-foreground uppercase tracking-wide">Join the Community</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">50,000+ reviewers across India already earning.</p>
              <div className="flex -space-x-2">
                {[
                'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&q=80',
                'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&q=80',
                'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&q=80'].
                map((src, i) =>
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white overflow-hidden">
                    <AppImage src={src} alt="Reviewer profile" width={32} height={32} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="w-8 h-8 rounded-full border-2 border-white cta-gradient flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white">50K</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}